import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { toast } from "react-toastify";

import {
  getUserPersonalInfo,
  editProfileDataAPI,
} from "../../../../api/userApi";
import { useTipStyles } from "./styles";

export interface SubscriptionTier {
  id: string;
  tierName: string;
  price: number;
  durationInDays: 30 | 90 | 180;
}
export interface EarningsHistoryItem {
  amount: number;
  type: "subscription" | "ppv" | "custom" | "tip";
  date: Date;
  payoutStatus: boolean;
}
export interface UserPricingData {
  subscriptionPrice: SubscriptionTier[];
  ppvRate: number;
  customContentPrice: number;
  minimumTipAmount: number; // Змінено з tipMinimum
  suggestedTips: number[]; // Змінено з suggestedTipAmounts
  earningsHistory: EarningsHistoryItem[];
}
interface TipSettings {
  minimumAmount: number;
  suggestedAmounts: number[];
  allowCustomAmount: boolean;
}

const TipMinimums: React.FC = () => {
  const classes = useTipStyles();
  const [tipSettings, setTipSettings] = useState<TipSettings>({
    minimumAmount: 2.0,
    suggestedAmounts: [],
    allowCustomAmount: true,
  });
  const [newAmount, setNewAmount] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getUserPersonalInfo();
        const userData = res.data as UserPricingData;
        setTipSettings({
          minimumAmount: userData.minimumTipAmount || 2.0, // Змінено з tipMinimum
          suggestedAmounts: userData.suggestedTips || [], // Змінено з suggestedTipAmounts
          allowCustomAmount: true,
        });
      } catch (error) {
        toast.error("Failed to load tip settings");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const updateMinimumAmount = (amount: number) => {
    const newAmount = Math.max(0, Number(amount));
    setTipSettings(prev => ({
      ...prev,
      minimumAmount: newAmount,
      suggestedAmounts: prev.suggestedAmounts.filter(tip => tip >= newAmount),
    }));
  };

  const addSuggestedAmount = () => {
    const amount = parseFloat(newAmount);
    if (
      amount &&
      amount > tipSettings.minimumAmount &&
      !tipSettings.suggestedAmounts.includes(amount)
    ) {
      setTipSettings({
        ...tipSettings,
        suggestedAmounts: [...tipSettings.suggestedAmounts, amount].sort(
          (a, b) => a - b
        ),
      });
      setNewAmount("");
    } else {
      toast.error("Please enter a valid amount above the minimum tip.");
    }
  };

  const removeSuggestedAmount = (amount: number) => {
    setTipSettings({
      ...tipSettings,
      suggestedAmounts: tipSettings.suggestedAmounts.filter(a => a !== amount),
    });
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("minimumTipAmount", tipSettings.minimumAmount.toString());
    tipSettings.suggestedAmounts.forEach((amount, index) => {
      formData.append(`suggestedTips[${index}]`, amount.toString());
    });

    try {
      const response = await editProfileDataAPI(formData);
      if (response.success) {
        // Перевірка success замість status
        toast.success("Tip settings updated successfully");
        setTipSettings(prev => ({
          ...prev,
          minimumAmount: prev.minimumAmount,
        }));
      } else {
        throw new Error(
          `Failed with message: ${response.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error details:", error);
      toast.error("Failed to update tip settings");
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>Tip Minimums</Typography>
      <Typography className={classes.infoText}>
        Set minimum tip amounts and suggested tip values for your followers.
      </Typography>

      <Card className={classes.settingsCard}>
        <CardContent className={classes.cardContent}>
          <Box className={classes.formRow}>
            <Typography className={classes.label}>
              Minimum Tip Amount
            </Typography>
            <TextField
              className={classes.inputField}
              variant="outlined"
              size="small"
              type="number"
              value={tipSettings.minimumAmount}
              onChange={e => updateMinimumAmount(Number(e.target.value))}
              placeholder="2.00"
              InputProps={{
                startAdornment: (
                  <Typography style={{ color: "#f06ec4", marginRight: "4px" }}>
                    $
                  </Typography>
                ),
              }}
            />
          </Box>

          <Box className={classes.suggestedAmountsContainer}>
            <Typography className={classes.suggestedAmountsTitle}>
              Suggested Tip Amounts
            </Typography>
            <Typography
              className={classes.infoText}
              style={{ marginBottom: "16px" }}
            >
              These amounts will appear as quick-select buttons for your
              followers.
            </Typography>

            <Box className={classes.amountsGrid}>
              {tipSettings.suggestedAmounts.map(amount => (
                <Chip
                  key={amount}
                  label={`$${amount}`}
                  className={classes.amountChip}
                  onDelete={() => removeSuggestedAmount(amount)}
                  deleteIcon={<DeleteIcon style={{ color: "#f06ec4" }} />}
                />
              ))}
            </Box>

            <Box className={classes.addAmountContainer}>
              <TextField
                className={classes.addAmountInput}
                variant="outlined"
                size="small"
                type="number"
                value={newAmount}
                onChange={e => setNewAmount(e.target.value)}
                placeholder="25"
                InputProps={{
                  startAdornment: (
                    <Typography
                      style={{ color: "#f06ec4", marginRight: "4px" }}
                    >
                      $
                    </Typography>
                  ),
                }}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") addSuggestedAmount();
                }}
                inputProps={{ min: tipSettings.minimumAmount }}
              />
              <Button
                className={classes.addButton}
                onClick={addSuggestedAmount}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
          </Box>

          <Box className={classes.customTipSection}>
            <Typography className={classes.customTipTitle}>
              Custom Tip Amounts
            </Typography>
            <Typography className={classes.customTipDescription}>
              Followers can always enter custom tip amounts above your minimum.
              This setting is always enabled to maximize your earning potential.
            </Typography>
            <Typography
              style={{ color: "#4CAF50", fontSize: "14px", fontWeight: 500 }}
            >
              ✓ Custom amounts enabled
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Button className={classes.saveButton} onClick={handleSave}>
        Save Changes
      </Button>
    </div>
  );
};

export default TipMinimums;
